import type { VariantProps } from 'class-variance-authority';
import { Loader2Icon } from 'lucide-react';
import type * as React from 'react';
import { Button, type buttonVariants } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/cn';

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
  // Shown only when the button is in a disabled state
  disabledTooltipText?: string;
  disabledTooltipPosition?: 'bottom' | 'left' | 'right' | 'top' | undefined;
}

// https://wds-shadcn-registry.netlify.app/components/loading-swap/
/**
 * Swaps child content with a loading spinner without changing the element size
 * @example
 * <Button>
 *   <LoadingSwap isLoading={isLoading}>Click Me</LoadingSwap>
 * </Button>
 */
const LoadingSwap = ({
  isLoading,
  children,
  className,
}: {
  isLoading: boolean;
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <div className="grid grid-cols-1 items-center justify-items-center">
      <div
        className={cn(
          'col-start-1 col-end-2 row-start-1 row-end-2 w-full',
          isLoading ? 'invisible' : 'visible',
          className,
        )}
      >
        {children}
      </div>
      <div
        className={cn(
          'col-start-1 col-end-2 row-start-1 row-end-2',
          isLoading ? 'visible' : 'invisible',
          className,
        )}
      >
        <Loader2Icon className="animate-spin" />
      </div>
    </div>
  );
};

export const ActionButton = ({
  disabledTooltipPosition,
  disabledTooltipText,
  loading = false,
  ...props
}: ButtonProps) => {
  if (props.asChild) {
    return (
      <Button {...props} disabled={loading || props.disabled}>
        {props.children}
      </Button>
    );
  }

  const button = (
    <Button {...props} disabled={loading || props.disabled}>
      <LoadingSwap isLoading={loading} className="inline-flex items-center gap-2">
        {props.children}
      </LoadingSwap>
    </Button>
  );

  // If `disabledTooltip` is passed in, wrap the <Button /> in a <Tooltip />.
  if (!!disabledTooltipText && props.disabled) {
    return (
      <Tooltip>
        {/* `asChild` ensures that the TooltipTrigger doesn't render a button, which causes nested buttons */}
        <TooltipTrigger asChild>
          <span>{button}</span>
        </TooltipTrigger>
        <TooltipContent side={disabledTooltipPosition ?? 'bottom'} sideOffset={4}>
          {disabledTooltipText}
        </TooltipContent>
      </Tooltip>
    );
  }

  return button;
};
